import React, { useEffect, useState } from 'react';
import { useBreadcrumbs, useUser } from '../hooks';
import { Header, Table, Groupper, Button, Modal, Message, Toast, Label, Icon, Input } from 'react-frontier';
import { addCommas } from '../SiteUtil';
import classNames from 'classnames';
import moment from 'moment';
import API from '../API';
import UserAccess from '../UserAccess';
import { EmploymentLogType, SetLoading } from '../SiteClasses';
import EmploymentAdjustmentModal from '../components/EmploymentAdjustmentModal';

interface WeeklyPayment{
	user_id: number,
	name: string,
	logs: number,
	amount: number,
	has_account: number,
}

interface WeeklyPaymentLog{
	log_id: number,
	employment_name: string,
	salary: number,
	time_start: number,
	time_end: number,
	date_start: number,
	date_end: number,
	type_id: number,
	log_type_id: number,
}

var formatTime = (mins: number)=> mins === null || mins === undefined
	? '---'
	: `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

var logType = (l: WeeklyPaymentLog)=> l.log_type_id === EmploymentLogType.ADJUSTMENT
	? 'Ajuste'
	: l.log_type_id ? 'Incidencia' : (l.type_id === 2 ? 'Descanso' : 'Laboral');

var getLogTypeColor = (l: WeeklyPaymentLog)=>{
	if(l.log_type_id === EmploymentLogType.ADJUSTMENT) return 'yellow';
	if(!l.log_type_id && l.type_id !== 2) return 'green';
	return 'orange';
};

var EmploymentPayments = ()=>{
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var canConfirm = hasAccess(UserAccess.EMPLOYMENTS.CONFIRM_PAYMENTS);
	var canAdjust = hasAccess(UserAccess.EMPLOYMENTS.EDIT_EMPLOYMENTS);
	var canDeleteLogs = hasAccess(UserAccess.EMPLOYMENTS.DELETE_EMPLOYMENTS_RECORDS);
	var [loadError, setLoadError] = useState<string>(null);
	var [payments, setPayments] = useState<WeeklyPayment[]>(null);
	var [selectedUser, setSelectedUser] = useState<WeeklyPayment>(null);
	var [userLogs, setUserLogs] = useState<WeeklyPaymentLog[]>(null);
	var [selectedLogs, setSelectedLogs] = useState<number[]>([]);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [deletePrompts, setDeletePrompts] = useState<string[]>(null);
	var [logToDelete, setLogToDelete] = useState<WeeklyPaymentLog>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
	var [search, setSearch] = useState('');
	var currentWeekStart = moment().subtract(1, 'week').startOf('isoWeek');

	var weekStart = currentWeekStart.clone().startOf('day').unix();
	var weekEnd = currentWeekStart.clone().add(6, 'days').endOf('day').unix();
	var isMonday = moment().isoWeekday() === 1;

	var loadPayments = ()=>{
		setPayments(null);
		setLoadError(null);
		API.getWeeklyPayments(weekEnd).then(res=>{
			if(res.error) return setLoadError(res.message);
			setPayments(res.data);
		}).catch(()=>{
			setLoadError('Hubo un error cargando los pagos pendientes (LCL-1)');
		});
	};

	useEffect(()=> setTitle('Pagos'), []);

	useEffect(()=>{
		loadPayments();
	}, []);

	var formatWeekRange = ()=>
		`${currentWeekStart.format('DD/MM/YYYY')} - ${currentWeekStart.clone().add(6, 'days').format('DD/MM/YYYY')}`;

	var openUser = (user: WeeklyPayment)=>{
		setSelectedUser(user);
		setUserLogs(null);
		setSelectedLogs([]);
		setModalPrompts(null);
		API.getUserWeeklyLogs(user.user_id, weekEnd).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setUserLogs(res.data);
		}).catch(()=>{
			setModalPrompts(['Hubo un error cargando los registros del empleado (LCL-2)']);
		});
	};

	var toggleLog = (log_id: number)=>{
		setSelectedLogs(prev=>{
			var arr = [...prev];
			var ix = arr.indexOf(log_id);
			if(ix === -1) arr.push(log_id);
			else arr.splice(ix, 1);
			return arr;
		});
	};

	var confirm = ()=>{
		if(!isMonday) return setModalPrompts(['Los pagos solo se pueden confirmar los lunes.']);
		if(selectedLogs.length === 0) return setModalPrompts(['Selecciona al menos un registro para confirmar.']);
		setLoading(true);
		API.confirmWeeklyPayments(selectedLogs).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Pagos confirmados!');
			setSelectedUser(null);
			loadPayments();
		}).catch(()=>{
			setModalPrompts(['Hubo un error confirmando los pagos (CNF-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var openDeleteLog = (log: WeeklyPaymentLog)=>{
		setDeletePrompts(null);
		setLogToDelete(log);
	};

	var deleteLog = (setLoading: SetLoading)=>{
		if(!logToDelete) return;
		setLoading(true);
		API.deleteWeeklyPaymentLog(logToDelete.log_id).then(res=>{
			if(res.error) return setDeletePrompts([res.message]);
			Toast.success('Registro eliminado.');
			setSelectedLogs(prev => prev.filter(id => id !== logToDelete.log_id));
			setUserLogs(prev => (prev || []).filter(l => l.log_id !== logToDelete.log_id));
			setPayments(prev => {
				if(!prev) return prev;
				return prev.map(p => p.user_id === selectedUser.user_id ? {
					...p,
					logs: p.logs - 1,
					amount: (p.amount || 0) - (logToDelete.salary || 0),
				} : p).filter(p => p.logs > 0);
			});
			setLogToDelete(null);
		}).catch(()=>{
			setDeletePrompts(['Hubo un error eliminando el registro (DEL-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onAdjustmentCreated = (payment: WeeklyPayment, logs: WeeklyPaymentLog[])=>{
		setPayments(prev => {
			if(!prev) return prev;
			var next = prev.filter(p => p.user_id !== payment?.user_id);
			if(payment){
				next.push(payment);
				next.sort((a, b)=> a.name.localeCompare(b.name));
			}
			return next;
		});

		if(selectedUser?.user_id === payment?.user_id){
			setSelectedUser(payment);
			setUserLogs(logs || []);
		}
	};
	var pendingLogs = (userLogs || []).filter(log => log.date_start < weekStart);
	var weekLogs = (userLogs || []).filter(log => log.date_start >= weekStart);
	var regularLogs = weekLogs.filter(log => log.log_type_id !== EmploymentLogType.ADJUSTMENT);
	var adjustmentLogs = weekLogs.filter(log => log.log_type_id === EmploymentLogType.ADJUSTMENT);
	var logsTotal = (userLogs || []).reduce((sum, log)=> sum + (log.salary || 0), 0);
	var requiresAccount = (userLogs || []).some(log => selectedLogs.indexOf(log.log_id) !== -1 && (log.salary || 0) !== 0);

	var renderLog = (l: WeeklyPaymentLog)=>{
		var selected = selectedLogs.indexOf(l.log_id) !== -1;
		return <Table.Row key={`PAYLOG-${l.log_id}`} selectable onClick={()=> toggleLog(l.log_id)}>
			<Table.Cell collapsing>
				<i className={classNames('large icon circle', { check: selected, outline: !selected })}></i>
			</Table.Cell>
			<Table.Cell>{moment.unix(l.date_start).format('DD/MM/YYYY')}</Table.Cell>
			<Table.Cell>{l.employment_name}</Table.Cell>
			<Table.Cell>
				<Label style={{ width: 90, textAlign: 'center', padding: '2px 6px', fontSize: 11 }} color={getLogTypeColor(l)}>
					{logType(l)}
				</Label>
			</Table.Cell>
			<Table.Cell>{formatTime(l.time_start)}</Table.Cell>
			<Table.Cell>{formatTime(l.time_end)}</Table.Cell>
			<Table.Cell>${addCommas(l.salary || 0)}</Table.Cell>
			<Table.Cell collapsing>
				{canDeleteLogs && (
					<Button icon basic size='tiny' iconName='trash' nativeOnClick={(ev)=>{
						ev.stopPropagation();
						openDeleteLog(l);
					}} />
				)}
			</Table.Cell>
		</Table.Row>;
	};

	var renderSeparator = (key: string, text?: string)=> <Table.Row key={key} className='noselect'>
		<Table.Cell colSpan={8} style={{ background: '#f3f3f3', padding: '0.45rem', fontWeight: 'bold', fontSize: 12 }}>
			{text || <>&nbsp;</>}
		</Table.Cell>
	</Table.Row>;

	if(loadError) return <Header text='Error' subtext={loadError} />

	var shownPayments = !payments ? null : payments.filter(payment=>{
		var query = search.trim().toLowerCase();
		if(!query) return true;

		return [
			payment.name,
			payment.user_id,
		].filter(Boolean).join(' ').toLowerCase().includes(query);
	});

	return <div>
		<Groupper width={700} style={{ marginTop: 20 }}
			title={
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					Pagos pendientes
					<div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
						<div style={{ fontSize: 14, fontWeight: 500 }}>
							{formatWeekRange()}
						</div>
						{canAdjust && <Button iconName='dollar' text='Ajuste' size='small' color='green' onClick={()=> setShowAdjustmentModal(true)} />}
					</div>
				</div>
			}
		>
			{!payments ? (
				<Header text='Cargando pagos pendientes' loading style={{ margin: 'auto', marginTop: 60, marginBottom: 40 }} centered />
			) : (
				<>
					<Input placeholder='Buscar...' value={search} onChange={setSearch} />
					<Table
						striped fitted selectable
						className='first last'
						style={{ marginTop: 20 }}
						headers={['Empleado', 'Logs', 'A pagar', 'Cuenta']}
						collapsingIndexes={[1, 2, 3]}
						centeredIndexes={[1, 2, 3]}
						onClick={(v, i)=> openUser(shownPayments[i])}
						footer={shownPayments.length !== 0 &&
							<Table.Row>
								<Table.Cell colSpan={2} style={{ textAlign: 'right', fontSize: 14, fontWeight: 'bold' }}>
									Total:
								</Table.Cell>
								<Table.Cell collapsing colSpan={1} style={{ textAlign: 'start', fontSize: 14, fontWeight: 'bold' }}>
									${addCommas(shownPayments.reduce((acc, p) => acc + Number(p.amount || 0), 0))}
								</Table.Cell>
								<Table.Cell collapsing />
							</Table.Row>
						}
						data={shownPayments.length !== 0 ? shownPayments.map(p=>[
							p.name,
							p.logs,
							'$' + addCommas(p.amount || 0),
							p.has_account
								? <Icon name='check' color='green' />
								: <Icon name='close' color='red' />
						]) : null}
					>
						{shownPayments.length === 0 && (
							<Table.Cell row className='empty' colSpan={4}>
								{search.trim() ? 'No se encontraron registros' : 'No hay pagos pendientes esta semana'}
							</Table.Cell>
						)}
					</Table>
				</>
			)}
		</Groupper>
		<Modal open={!!selectedUser} size='large' onClose={()=> setSelectedUser(null)}>
			<Modal.Header>Confirmar pagos {selectedUser ? `- ${selectedUser.name}` : ''}</Modal.Header>
			<Modal.Content style={{ maxHeight: 650, msOverflowY: 'auto', overflowX: 'hidden' }}>
				{!userLogs ? (
					<Header loading text='Cargando registros...' />
				) : (
					<>
						<div style={{ textAlign: 'center', marginBottom: 10 }}>
							<Button text='Seleccionar todos' color='black' onClick={()=>{
								setSelectedLogs(selectedLogs.length === userLogs.length ? [] : userLogs.map(l=> l.log_id));
							}}/>
						</div>
						<Table
							selectable fitted
							collapsingIndexes={[0, 1, 3, 4, 5, 6, 7]}
							centeredIndexes={[0, 3, 4, 5, 6, 7]}
							style={{ marginTop: 20 }}
							headers={[
								<div style={{ textAlign: 'center' }}><i className='checkmark icon' style={{ marginRight: 0 }}></i></div>,
								'Fecha',
								'Empleo',
								'Tipo',
								'Inicio',
								'Fin',
								'Salario',
								''
							]}
						>
							{userLogs.length === 0 ? (
								<Table.Cell row className='empty' colSpan={8}>
									No hay registros pendientes para esta semana
								</Table.Cell>
							) : (
								<>
									{weekLogs.length > 0 && renderSeparator('PAYLOG-SEPARATOR-WEEK', `Semana anterior (${formatWeekRange()})`)}
									{regularLogs.map(renderLog)}
									{regularLogs.length > 0 && adjustmentLogs.length > 0 && renderSeparator('PAYLOG-SEPARATOR')}
									{adjustmentLogs.map(renderLog)}
									{pendingLogs.length > 0 && renderSeparator('PAYLOG-SEPARATOR-PENDING', 'Pendientes (semanas anteriores)')}
									{pendingLogs.map(renderLog)}
									<Table.Row>
										<Table.Cell colSpan={6} style={{ textAlign: 'right', fontSize: 14, fontWeight: 'bold' }}>
											A pagar:
										</Table.Cell>
										<Table.Cell collapsing style={{ textAlign: 'start', fontSize: 14, fontWeight: 'bold' }}>
											${addCommas(logsTotal)}
										</Table.Cell>
										<Table.Cell collapsing />
									</Table.Row>
								</>
							)}
						</Table>
					</>
				)}
				{!isMonday && (
					<Message type='warning' centered text='Los pagos solo se confirman los lunes. Los registros de la semana en curso se muestran para revisión.' />
				)}
				{selectedUser && requiresAccount && !selectedUser.has_account && (
					<Message type='warning' centered text='El empleado no tiene una cuenta bancaria registrada, no se pueden confirmar sus pagos.' />
				)}
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=> setSelectedUser(null)} />
				{canConfirm && (
					<Button text='Confirmar' color='black' loading={loading} disabled={!isMonday || !userLogs || selectedLogs.length === 0 || (requiresAccount && !selectedUser?.has_account)} onClick={confirm} />
				)}
			</Modal.Actions>
		</Modal>
		<Modal open={!!logToDelete} size='tiny' onClose={()=> setLogToDelete(null)}>
			<Modal.Header>Eliminar registro</Modal.Header>
			<Modal.Content>
				<Header text='¿Eliminar este registro?' subtext='El registro dejará de contar para el pago de la semana.' />
				{logToDelete && (
					<Table className='ar last' style={{ marginTop: 15 }} fitted striped details data={[
						['Fecha', moment.unix(logToDelete.date_start).format('DD/MM/YYYY')],
						['Empleo', logToDelete.employment_name],
						['Tipo', logType(logToDelete)],
						['Salario', `$${addCommas(logToDelete.salary || 0)}`],
					]} />
				)}
				<Message type='error' list={deletePrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={()=> setLogToDelete(null)} />
				<Button text='Eliminar' color='red' onClick={deleteLog} />
			</Modal.Actions>
		</Modal>
		<EmploymentAdjustmentModal
			open={showAdjustmentModal}
			onClose={()=> setShowAdjustmentModal(false)}
			weekStart={weekStart}
			weekEnd={weekEnd}
			onCreated={onAdjustmentCreated}
		/>
	</div>
}

export default EmploymentPayments;
